import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { updateUserRole, updateUserDetails, banUser, unbanUser } from "@/api/admin";

export const useAdminMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const updateRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: 'user' | 'admin' }) => {
      return await updateUserRole(userId, role);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["adminUserList"] });
      toast({
        title: "Role updated",
        description: `User role has been changed to ${variables.role} successfully.`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error updating role",
        description: error.message || "Failed to update user role",
      });
    },
  });

  const updateDetails = useMutation({
    mutationFn: async ({ 
      userId, 
      updates 
    }: { 
      userId: string; 
      updates: {
        subscription_status?: string;
        is_banned?: boolean;
      }
    }) => {
      return await updateUserDetails(userId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUserList"] });
      toast({
        title: "User updated",
        description: "User details have been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error updating user",
        description: error.message || "Failed to update user details",
      });
    },
  });

  const banUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await banUser(userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUserList"] });
      toast({
        title: "User banned",
        description: "User has been banned successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error banning user",
        description: error.message || "Failed to ban user",
      });
    },
  });

  const unbanUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await unbanUser(userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUserList"] });
      toast({
        title: "User unbanned",
        description: "User has been unbanned successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error unbanning user",
        description: error.message || "Failed to unban user",
      });
    },
  });

  return {
    updateRole,
    updateDetails,
    banUserMutation,
    unbanUserMutation,
  };
};
